import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeftIcon,
  CheckBadgeIcon,
  CircleStackIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import Table from "./components/ui/Table";
import Pagination from "./components/ui/Pagination";
import Spinner from "./components/ui/Spinner";
import StatCard from "./components/ui/StatCard";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const baseUrl = process.env.REACT_APP_API_BASE_URL;
const ASSET_HOST = baseUrl.replace("/admin", "");
const ITEMS_PER_PAGE = 10;

const TASK_TYPE_LABELS = {
  telegram: "Telegram",
  youtube: "YouTube",
  download_app: "Download App",
  website_visit: "Website Visit",
};

const resolveImage = (image) => {
  if (!image) return null;
  if (image.startsWith("http")) return image;
  return `${ASSET_HOST}/${image.replace(/^\//, "")}`;
};

const TaskHistoryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [task, setTask] = useState(null);
  const [totalCompleted, setTotalCompleted] = useState(0);
  const [dayWise, setDayWise] = useState([]);
  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchHistory = async (p = 1) => {
    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}/task/history/${id}?page=${p}&limit=${ITEMS_PER_PAGE}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (!res.ok || !result.status) throw new Error(result.message || "Failed to fetch task history");

      setTask(result.data.task);
      setTotalCompleted(result.data.totalCompleted || 0);
      setDayWise(result.data.dayWise || []);
      setHistory(result.data.history || []);
      setTotalPages(result.data.pagination?.lastPage || 1);
      setPage(result.data.pagination?.currentPage || 1);
    } catch (err) {
      setError(err.message);
      toast.error(err.message || "Failed to fetch task history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const chartData = {
    labels: dayWise.map((d) => d.date),
    datasets: [
      {
        label: "Completions",
        data: dayWise.map((d) => d.count),
        backgroundColor: "#28A265c0",
        borderColor: "#28A265",
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const columns = [
    { key: "userEmail", header: "User" },
    {
      key: "status",
      header: "Status",
      render: (item) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            item.status === "completed" ? "bg-emerald-500/20 text-emerald-300" : "bg-orange-500/20 text-orange-300"
          }`}
        >
          {item.status}
        </span>
      ),
    },
    { key: "rewardCoin", header: "Reward Coin" },
    {
      key: "completedAt",
      header: "Completed At",
      render: (item) => new Date(item.completedAt).toLocaleString(),
    },
  ];

  return (
    <div className="flex flex-col md:flex-row bg-gray-900 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-6">
          <button
            onClick={() => navigate("/tasks")}
            className="flex items-center gap-2 text-gray-300 hover:text-white mb-6 transition"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Tasks
          </button>

          {loading && !task ? (
            <div className="flex justify-center py-20">
              <Spinner size="lg" />
            </div>
          ) : error && !task ? (
            <p className="text-red-400">{error}</p>
          ) : task ? (
            <>
              {/* Task summary */}
              <div className="flex flex-col sm:flex-row items-start gap-4 bg-gray-800 rounded-lg p-6 shadow mb-6">
                {resolveImage(task.image) ? (
                  <img
                    src={resolveImage(task.image)}
                    alt={task.title}
                    className="h-20 w-20 object-cover rounded-lg border border-gray-700"
                  />
                ) : (
                  <div className="h-20 w-20 flex items-center justify-center rounded-lg bg-gray-900 text-gray-500 text-xs italic">
                    No Image
                  </div>
                )}
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-white">{task.title}</h2>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-300">
                      {TASK_TYPE_LABELS[task.taskType] || task.taskType}
                    </span>
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        task.isActive ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"
                      }`}
                    >
                      {task.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                  {task.description && <p className="text-gray-400 text-sm mt-2">{task.description}</p>}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
                <StatCard icon={CheckBadgeIcon} label="Total Completed" value={totalCompleted} />
                <StatCard icon={CircleStackIcon} label="Reward Coin" value={task.rewardCoin} />
                <StatCard icon={CalendarDaysIcon} label="Days With Activity" value={dayWise.length} />
              </div>

              {/* Day-wise chart */}
              {dayWise.length > 0 && (
                <div className="bg-gray-800 rounded-lg p-6 shadow mb-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Completions by Day</h3>
                  <Bar
                    data={chartData}
                    options={{
                      responsive: true,
                      plugins: { legend: { display: false } },
                      scales: {
                        x: { grid: { color: "rgba(255,255,255,0.1)" }, ticks: { color: "white" } },
                        y: {
                          grid: { color: "rgba(255,255,255,0.1)" },
                          ticks: { color: "white", precision: 0 },
                          beginAtZero: true,
                        },
                      },
                    }}
                  />
                </div>
              )}

              {/* History table */}
              <h3 className="text-lg font-semibold text-white mb-4">Completion Records</h3>
              <Table
                columns={columns}
                rows={history}
                rowKey={(item) => item.id}
                loading={loading}
                emptyMessage="No completion records found."
              />

              <div className="mt-6">
                <Pagination page={page} totalPages={totalPages} onPageChange={fetchHistory} />
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default TaskHistoryPage;
